import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { loadItineraryShareContent, loadItineraryTableShareContent, openItineraryShare, recordItineraryShareView } from "../repo/itineraryShareRepo";
import { createShareOpenEventId, shareErrorCode } from "../service/itineraryShareService";
import type { PublicItinerary } from "../types";
import type { ItineraryTableSource } from "../types/itineraryTable";

/** 编排客户分享的授权、正文读取和单次打开计数。 */
export function useItineraryShare(shareId: string, options: { initialEventId?: string; initialErrorCode?: string } = {}) {
  const eventRef = useRef<string | undefined>(options.initialEventId);
  const shareRef = useRef(shareId);
  const previousShareIdRef = useRef(shareId);
  if (shareRef.current !== shareId) {
    shareRef.current = shareId;
    eventRef.current = createShareOpenEventId();
  }
  const [eventNonce, setEventNonce] = useState(0);
  const [oauthError, setOauthError] = useState<string | undefined>(options.initialErrorCode);
  const eventId = eventRef.current ?? (eventRef.current = createShareOpenEventId());
  const openQuery = useQuery({
    queryKey: ["itinerary", "share", "open", shareId, eventId, eventNonce],
    enabled: Boolean(shareId) && !oauthError,
    retry: false,
    queryFn: () => openItineraryShare(shareId, eventId),
  });
  const authorized = openQuery.data?.authorized === true;
  const viewType = openQuery.data?.view_type ?? "detail";
  const contentQuery = useQuery<PublicItinerary | ItineraryTableSource>({
    queryKey: ["itinerary", "share", "content", shareId, eventId, viewType],
    enabled: authorized,
    retry: false,
    queryFn: () => viewType === "table" ? loadItineraryTableShareContent(shareId) : loadItineraryShareContent(shareId),
  });
  const viewQuery = useQuery({
    queryKey: ["itinerary", "share", "view", shareId, eventId],
    enabled: authorized && contentQuery.isSuccess,
    retry: 2,
    queryFn: () => recordItineraryShareView(shareId, eventId),
  });
  useEffect(() => {
    if (openQuery.error) setOauthError(shareErrorCode(openQuery.error));
  }, [openQuery.error]);
  useEffect(() => {
    if (previousShareIdRef.current !== shareId) {
      previousShareIdRef.current = shareId;
      setOauthError(undefined);
    }
  }, [shareId]);
  const beginWithEvent = (nextEvent: string) => {
    if (eventRef.current === nextEvent) return;
    eventRef.current = nextEvent;
    setOauthError(undefined);
    setEventNonce((value) => value + 1);
  };
  return {
    eventId,
    openQuery,
    contentQuery,
    viewQuery,
    authorizationUrl: openQuery.data?.authorization_url ?? undefined,
    errorCode: oauthError ?? shareErrorCode(openQuery.error) ?? shareErrorCode(contentQuery.error) ?? shareErrorCode(viewQuery.error),
    beginWithEvent,
  };
}
